import { buildHouseHierarchy } from './houseHierarchy.js'
import { resolvedConcentrationDisplay } from './concentrationResolver.js'

const clean = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
const concentration = value => ({
  'eau de parfum': 'edp', 'eau de toilette': 'edt', 'eau de cologne': 'edc',
  'extrait de parfum': 'extrait',
}[clean(value)] || clean(value))

export function searchRankMap(rows = []) {
  return new Map(rows.map((row, index) => [row.id, index]))
}

export function searchRankOf(ranks, row) {
  if (!row?.id || !ranks.has(row.id)) return Number.POSITIVE_INFINITY
  return ranks.get(row.id)
}

// Exact catalog name is the fragrance identity inside a derived line.
// Sauvage EDT/EDP stay one fragrance; Sauvage Elixir is a sibling.
export function groupLineFragrances(variants = [], ranks = new Map()) {
  const groups = new Map()
  for (const row of variants) {
    const key = clean(row?.name)
    if (!key) continue
    if (!groups.has(key)) groups.set(key, { name: row.name, variants: [] })
    groups.get(key).variants.push(row)
  }
  return [...groups.values()]
    .map(group => ({
      ...group,
      variants: [...group.variants].sort((a, b) => searchRankOf(ranks, a) - searchRankOf(ranks, b)),
    }))
    .sort((a, b) => searchRankOf(ranks, a.variants[0]) - searchRankOf(ranks, b.variants[0]))
}

// Keep the search service's relevance order while retaining browse grouping.
export function buildSearchHierarchy(rows = []) {
  const ranks = searchRankMap(rows)
  const lineRank = line => Math.min(...line.variants.map(row => searchRankOf(ranks, row)))
  return buildHouseHierarchy(rows)
    .map(house => ({
      ...house,
      lines: house.lines
        .map(line => {
          const variants = [...line.variants].sort((a, b) => searchRankOf(ranks, a) - searchRankOf(ranks, b))
          return { ...line, variants, fragrances: groupLineFragrances(variants, ranks) }
        })
        .sort((a, b) => lineRank(a) - lineRank(b)),
    }))
    .sort((a, b) => lineRank(a.lines[0]) - lineRank(b.lines[0]))
}

export function catalogRowIsOwned(row, collection = []) {
  const resolved = concentration(resolvedConcentrationDisplay(row))
  return collection.some(owned => {
    if (owned.deleted_at) return false
    if (row.id && owned.catalog_id) return row.id === owned.catalog_id
    return resolved && resolved !== 'unknown'
      && clean(row.name) === clean(owned.name)
      && clean(row.house) === clean(owned.house)
      && resolved === concentration(owned.concentration)
  })
}
